import {
	CopilotEntrypointType,
	CopilotStudyLevel,
	HashVersion,
	ICopilotServiceApplication,
	PromptVersion,
	RenderEntrypointCopilotCommand,
	RenderChatCopilotCommand,
	ChatType
} from 'interfaces';
import {  App as VueApp } from 'vue';
import { CopilotService } from '@/domain/copilot-service';
import { createStore } from '@/store';
import { IStore } from '@/interfaces';
import DependencyCheckerPlugin from '@/plugins/dependency-checker-plugin';

export class CopilotServiceApplication implements ICopilotServiceApplication {
	static Store: IStore;
	private vueApplicationBubble?: VueApp;
	private vueApplicationChat?: VueApp;

	constructor(private readonly copilotService: CopilotService) {	}

	public setCopilotHashVersion(version: HashVersion): void {
		this.copilotService.setHashVersion(version);
	}

	public setCopilotPromptVersion(version: PromptVersion): void {
		this.copilotService.setPromptVersion(version);
	}

	public renderEntrypoint(command: RenderEntrypointCopilotCommand): void {
		if (!command.detail.entrypointType) return;

		if (command.detail.promptVersion) this.setCopilotPromptVersion(command.detail.promptVersion);

		void this.mountVueApplicationOn(
			command.detail.element,
			command.detail.studyLevel,
			this.copilotService,
			command.detail.entrypointType,
			undefined,
			command.detail.contextData,
		);
	}

	public renderChat(command: RenderChatCopilotCommand): void {
		if (!command.detail.chatType) return;

		if (command.detail.promptVersion) this.setCopilotPromptVersion(command.detail.promptVersion);


		void this.mountVueApplicationOn(
			command.detail.element,
			command.detail.studyLevel,
			this.copilotService,
			undefined,
			command.detail.chatType,
			command.detail.contextData,
			command.detail.studyId,
		);
	}

	private async mountVueApplicationOn(
		root: HTMLElement | undefined,
		studyLevel: CopilotStudyLevel,
		copilotService: CopilotService,
		entrypointType?: CopilotEntrypointType,
		chatType?: ChatType,
		contextData?: { [key: string]: string },
		studyId?: number
	): Promise<void> {
		CopilotServiceApplication.Store = createStore();
		new DependencyCheckerPlugin().initialize();

		if (studyId) CopilotServiceApplication.Store.mutations.setActiveStudyId(studyId);

		if (entrypointType) await this.createBubble(studyLevel, copilotService, entrypointType, chatType, contextData);

		if (chatType) {
			root = this.mountDrawerInRoot(chatType, root)
			await this.createChat(studyLevel, copilotService, entrypointType, chatType, contextData);
		}

		const vueApplication = chatType ? this.vueApplicationChat : this.vueApplicationBubble;
		const { default: globalsPlugin } = await import("@/utils/vue-globals");

		vueApplication!.provide('store', CopilotServiceApplication.Store);
		vueApplication!.use(globalsPlugin);
		vueApplication!.mount(root);
	}

	private async createBubble(
		studyLevel: CopilotStudyLevel,
		copilotService: CopilotService,
		entrypointType?: CopilotEntrypointType,
		chatType?: ChatType,
		contextData?: { [key: string]: string }): Promise<void> {
		this.cleanPreviousInstancesBubble();

		const { default: app } = await import("@/presentation/App.vue");
		const { createApp, h } = await import('vue');

		this.vueApplicationBubble = createApp({
			render: () =>
				h(app, {
					entrypointType,
					chatType,
					contextData,
					studyLevel,
					copilotService,
				})
		});
	}

	private async createChat(
		studyLevel: CopilotStudyLevel,
		copilotService: CopilotService,
		entrypointType?: CopilotEntrypointType,
		chatType?: ChatType,
		contextData?: { [key: string]: string },
	): Promise<void> {
		this.cleanPreviousInstancesChat();

		const { default: app } = await import("@/presentation/App.vue");
		const { createApp, h } = await import("vue");

		this.vueApplicationChat = createApp({
			render: () =>
				h(app, {
					entrypointType,
					chatType,
					contextData,
					studyLevel,
					copilotService,
				})
		});
	}

	private cleanPreviousInstancesChat(): void {
		if (!this.vueApplicationChat) return;

		this.vueApplicationChat.unmount();
		this.vueApplicationChat = undefined;
	}

	private cleanPreviousInstancesBubble(): void {
		if (!this.vueApplicationBubble) return;

		this.vueApplicationBubble.unmount();
		this.vueApplicationBubble = undefined;
	}


	private mountDrawerInRoot(chatType: ChatType, root: HTMLElement | undefined): HTMLElement {
		if (root) return root;

		if ([ChatType.CHAT_IN_DRAWER, ChatType.BESTFIT].includes(chatType)) {
			const div = document.createElement('div');
			div.className = 'copilot-chat-container';
			document.body.appendChild(div);
			root = div;
		}

		if (!root) throw new Error('No root to mount app');

		return root;
	}
}
